import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { extractError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LoadingState, EmptyState, EventTime } from "@/components/Common";
import { StatusBadge, PriorityBadge, TaskStatusBadge } from "@/components/Badges";
import { RegistraContattoDialog } from "@/components/RegistraContattoDialog";
import { CaseDialog, TaskDialog, TaskCompletableList } from "@/components/CaseTaskDialogs";
import { AggiornaAnagraficaDialog } from "@/components/AggiornaAnagraficaDialog";
import { CustomFieldsForm, hasCustomFields } from "@/components/CustomFields";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { personName, fmtDateTime, fmtDate } from "@/lib/format";
import { Phone, Mail, MessageCircle, PhoneCall, PlusCircle, FolderPlus, ArrowLeft, Sparkles, Loader2, Download, FileText, Save, UserCog } from "lucide-react";
import { toast } from "sonner";

const TimelineFeed = ({ items }) => (
  items.length === 0 ? <EmptyState title="Nessun evento in timeline" /> : (
    <div className="space-y-0">
      {items.map((i) => (
        <div key={i.id} className="relative pl-10 pb-5 timeline-line" data-testid="persona-timeline-event">
          <div className="absolute left-2 top-1 h-6 w-6 rounded-full bg-[color:var(--brand-blue-100)] border-2 border-white flex items-center justify-center">
            <span className="h-2 w-2 rounded-full bg-[color:var(--brand-blue-600)]" />
          </div>
          <div className="rounded-lg border bg-white p-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-sm font-medium">{i.tipo}</span>
              <EventTime dataEvento={i.data_evento} createdAt={i.created_at} />
            </div>
            {i.oggetto && <div className="text-sm mt-1 font-medium text-slate-700">{i.oggetto}</div>}
            {i.descrizione && <div className="text-sm text-slate-600 mt-0.5 whitespace-pre-line">{i.descrizione}</div>}
            <div className="text-[11px] text-muted-foreground mt-1">{i.operatore_nome}{i.servizio_nome ? ` · ${i.servizio_nome}` : ""}</div>
          </div>
        </div>
      ))}
    </div>
  )
);

const PersonDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [caseOpen, setCaseOpen] = useState(false);
  const [taskOpen, setTaskOpen] = useState(false);
  const [anagOpen, setAnagOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [ai, setAi] = useState("");
  const [services, setServices] = useState([]);
  const [pExtra, setPExtra] = useState({});
  const [savingExtra, setSavingExtra] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try { const { data } = await api.get(`/persons/${id}`); setData(data); setPExtra(data?.persona?.campi_extra || {}); }
    catch (e) { toast.error(extractError(e)); } finally { if (!silent) setLoading(false); }
  }, [id]);
  useEffect(() => { load(); api.get("/services").then(({ data }) => setServices(data)).catch(() => {}); }, [load]);

  const saveExtra = async () => {
    setSavingExtra(true);
    try { await api.put(`/persons/${id}`, { campi_extra: pExtra }); toast.success("Campi aggiornati"); load(); }
    catch (e) { toast.error(extractError(e)); } finally { setSavingExtra(false); }
  };

  const genAi = async () => {
    setAiLoading(true);
    try { const { data } = await api.post(`/ai/person/${id}/summary`); setAi(data.summary); }
    catch (e) { toast.error(extractError(e)); } finally { setAiLoading(false); }
  };

  if (loading) return <LoadingState />;
  if (!data) return <EmptyState title="Persona non trovata" />;
  const p = data.persona;
  const wa = (p.whatsapp || p.telefono_principale || "").replace(/[^0-9]/g, "");
  const personDefs = Object.values(
    services.filter((s) => (p.servizi_ids || []).includes(s.id))
      .flatMap((s) => s.campi_personalizzati || [])
      .reduce((acc, f) => { if (f.chiave) acc[f.chiave] = f; return acc; }, {})
  );

  return (
    <div>
      <button onClick={() => navigate("/app/persone")} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-slate-900 mb-3"><ArrowLeft className="h-4 w-4" /> Persone</button>
      <Card className="p-5">
        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="h-16 w-16 rounded-2xl bg-[color:var(--brand-blue-600)] text-white flex items-center justify-center text-xl font-semibold shrink-0">
            {personName(p).split(" ").slice(0,2).map(s=>s[0]).join("")}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl font-semibold">{personName(p)}</h1>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground mt-1">
              {p.telefono_principale && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{p.telefono_principale}</span>}
              {p.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{p.email}</span>}
              {p.citta && <span>{p.indirizzo} {p.numero_civico}, {p.citta} ({p.provincia})</span>}
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(p.classificazioni||[]).map(c=><span key={c} className="text-[11px] rounded-full bg-[color:var(--brand-blue-100)] text-[color:var(--brand-blue-700)] px-2 py-0.5">{c}</span>)}
              {(p.tags||[]).map(t=><span key={t} className="text-[11px] rounded-full bg-slate-100 px-2 py-0.5">#{t}</span>)}
            </div>
            {(p.modalita_contatto || p.fascia_oraria) && <p className="text-xs text-muted-foreground mt-2">Preferenza contatto: {p.modalita_contatto || "-"} · Fascia: {p.fascia_oraria || "-"}</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {p.telefono_principale && <a href={`tel:${p.telefono_principale.replace(/\s/g,"")}`}><Button variant="outline" size="sm"><Phone className="h-4 w-4 mr-1" /> Telefono</Button></a>}
          {p.email && <a href={`mailto:${p.email}`}><Button variant="outline" size="sm"><Mail className="h-4 w-4 mr-1" /> Email</Button></a>}
          {wa && <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="text-[color:var(--brand-green-700)]"><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</Button></a>}
          {can("persons.write") && <Button size="sm" variant="secondary" onClick={() => setAnagOpen(true)} data-testid="update-anagrafica-button"><UserCog className="h-4 w-4 mr-1" /> Aggiorna anagrafica</Button>}
          {can("interactions.write") && <RegistraContattoDialog defaultPersonId={id} onDone={load} trigger={<Button size="sm" className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white"><PhoneCall className="h-4 w-4 mr-1" /> Registra contatto</Button>} />}
          {can("tasks.write") && <Button size="sm" variant="secondary" onClick={() => setTaskOpen(true)}><PlusCircle className="h-4 w-4 mr-1" /> Crea attività</Button>}
          {can("cases.write") && <Button size="sm" variant="secondary" onClick={() => setCaseOpen(true)} data-testid="open-case-button"><FolderPlus className="h-4 w-4 mr-1" /> Apri nuova pratica</Button>}
        </div>
      </Card>

      <Tabs defaultValue="panoramica" className="mt-5">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="panoramica">Panoramica</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="pratiche">Pratiche ({data.pratiche.length})</TabsTrigger>
          <TabsTrigger value="attivita">Attività ({data.attivita.length})</TabsTrigger>
          <TabsTrigger value="appuntamenti">Appuntamenti</TabsTrigger>
          <TabsTrigger value="documenti">Documenti</TabsTrigger>
          <TabsTrigger value="consensi">Consensi</TabsTrigger>
          <TabsTrigger value="ai">Riassunto AI</TabsTrigger>
        </TabsList>

        <TabsContent value="panoramica" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-4"><h3 className="font-semibold mb-2">Pratiche attive</h3>
              {data.pratiche.filter(c=>!["Conclusa","Archiviata","Annullata"].includes(c.stato)).slice(0,5).map(c=>(
                <button key={c.id} onClick={()=>navigate(`/app/pratiche/${c.id}`)} className="w-full text-left flex items-center justify-between gap-2 py-2 border-b last:border-0">
                  <span className="text-sm">{c.numero} · {c.servizio_nome}</span><StatusBadge stato={c.stato} /></button>
              )) }
              {data.pratiche.length===0 && <p className="text-sm text-muted-foreground">Nessuna pratica</p>}
            </Card>
            <Card className="p-4"><h3 className="font-semibold mb-2">Attività aperte</h3>
              {data.attivita.filter(t=>!["Completata","Annullata"].includes(t.stato)).slice(0,5).map(t=>(
                <div key={t.id} className="flex items-center justify-between gap-2 py-2 border-b last:border-0"><span className="text-sm">{t.titolo}</span><TaskStatusBadge stato={t.stato} /></div>
              ))}
              {data.attivita.length===0 && <p className="text-sm text-muted-foreground">Nessuna attività</p>}
            </Card>
          </div>

          <Card className="p-4 mt-4" data-testid="person-dati-associativi">
            <h3 className="font-semibold mb-2">Dati associativi</h3>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
              <div><dt className="text-xs text-muted-foreground">N. tessera</dt><dd className="font-medium">{p.numero_tessera || "-"}</dd></div>
              <div><dt className="text-xs text-muted-foreground">Codice ente affiliazione</dt><dd className="font-medium">{p.codice_ente_affiliazione || "-"}</dd></div>
            </dl>
          </Card>


          {hasCustomFields(personDefs, "persona") && (
            <Card className="p-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Campi aggiuntivi</h3>
                {can("persons.write") && (
                  <Button size="sm" variant="outline" onClick={saveExtra} disabled={savingExtra} data-testid="save-person-extra-button">
                    {savingExtra ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Salva campi
                  </Button>
                )}
              </div>
              <CustomFieldsForm defs={personDefs} values={pExtra} onChange={setPExtra} target="persona" disabled={!can("persons.write")} />
            </Card>
          )}
        </TabsContent>

        <TabsContent value="timeline" className="mt-4"><TimelineFeed items={data.timeline} /></TabsContent>

        <TabsContent value="pratiche" className="mt-4">
          {data.pratiche.length===0 ? <EmptyState title="Nessuna pratica" /> : (
            <Card className="divide-y">{data.pratiche.map(c=>(
              <button key={c.id} onClick={()=>navigate(`/app/pratiche/${c.id}`)} className="w-full text-left p-3 hover:bg-muted flex items-center justify-between gap-2">
                <div><div className="font-medium text-sm">{c.numero}</div><div className="text-xs text-muted-foreground">{c.servizio_nome} · {fmtDate(c.created_at)}</div></div>
                <div className="flex gap-2"><PriorityBadge priorita={c.priorita} /><StatusBadge stato={c.stato} /></div>
              </button>
            ))}</Card>
          )}
        </TabsContent>

        <TabsContent value="attivita" className="mt-4">
          <TaskCompletableList tasks={data.attivita} canWrite={can("tasks.write")} onChanged={() => load(true)} />
        </TabsContent>

        <TabsContent value="appuntamenti" className="mt-4">
          {data.appuntamenti.length===0 ? <EmptyState title="Nessun appuntamento" /> : (
            <Card className="divide-y">{data.appuntamenti.map(a=>(
              <div key={a.id} className="p-3"><div className="font-medium text-sm">{a.titolo}</div><div className="text-xs text-muted-foreground">{fmtDateTime(a.data_inizio)} · {a.luogo}</div></div>
            ))}</Card>
          )}
        </TabsContent>

        <TabsContent value="documenti" className="mt-4">
          {data.documenti.length===0 ? <EmptyState title="Nessun documento" /> : (
            <Card className="divide-y">{data.documenti.map(d=>(
              <div key={d.id} className="p-3 flex items-center justify-between gap-2"><div className="flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /><div><div className="text-sm font-medium">{d.titolo}</div><div className="text-xs text-muted-foreground">{d.categoria} · {fmtDate(d.created_at)}</div></div></div>
                <a href={`${api.defaults.baseURL}/documents/${d.id}/download`} onClick={(e)=>{e.preventDefault(); api.get(`/documents/${d.id}/download`,{responseType:'blob'}).then(r=>{const u=URL.createObjectURL(r.data); const a=document.createElement('a'); a.href=u; a.download=d.nome_file||'documento'; a.click();});}}><Button variant="ghost" size="sm"><Download className="h-4 w-4" /></Button></a>
              </div>
            ))}</Card>
          )}
        </TabsContent>

        <TabsContent value="consensi" className="mt-4">
          {(!data.consensi || data.consensi.length===0) ? <EmptyState title="Nessun consenso registrato" /> : (
            <Card className="divide-y">{data.consensi.map(c=>(
              <div key={c.id} className="p-3 flex items-center justify-between gap-2">
                <div><div className="text-sm font-medium">{c.finalita}</div><div className="text-xs text-muted-foreground">v{c.versione} · {c.modalita} · {fmtDateTime(c.data)}</div></div>
                <span className={`text-xs rounded-full px-2 py-0.5 ${c.accettato?"bg-[color:var(--brand-green-100)] text-[color:var(--brand-green-700)]":"bg-slate-100 text-slate-600"}`}>{c.accettato?"Accettato":"Non accettato/Revocato"}</span>
              </div>
            ))}</Card>
          )}
        </TabsContent>

        <TabsContent value="ai" className="mt-4">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-[color:var(--brand-orange-600)]" /> Riassunto AI della persona</h3>
              <Button size="sm" onClick={genAi} disabled={aiLoading} data-testid="ai-person-summary-button">{aiLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Genera</Button>
            </div>
            {ai ? <div className="prose prose-sm max-w-none whitespace-pre-line text-slate-700">{ai}<p className="text-[11px] text-muted-foreground mt-3">Contenuto generato dall'assistente AI. Verifica sempre le informazioni.</p></div>
              : <p className="text-sm text-muted-foreground">Premi "Genera" per ottenere una sintesi delle interazioni autorizzate.</p>}
          </Card>
        </TabsContent>
      </Tabs>

      <CaseDialog open={caseOpen} setOpen={setCaseOpen} personId={id} onDone={(c)=>navigate(`/app/pratiche/${c.id}`)} />
      <TaskDialog open={taskOpen} setOpen={setTaskOpen} personId={id} onDone={load} />
      <AggiornaAnagraficaDialog open={anagOpen} setOpen={setAnagOpen} person={p} onDone={load} />
    </div>
  );
};

export default PersonDetail;
